<template>
  <v-container fluid class="user-create-page">
    <v-card :loading="isLoading" outlined class="user-create-card">
      <div class="user-create-header">
        <div>
          <div class="page-title">{{ $t("pageHeader.editUser") }}</div>
          <div class="page-subtitle">
            Update profile, status, and access rights.
          </div>
        </div>

        <div class="header-actions" v-if="!User.superAdmin">
          <span class="active-label">{{ $t("labels.active") }}</span>
          <toggle-button
            :labels="true"
            v-model="User.active"
            :value="User.active"
            :sync="true"
          />
        </div>
      </div>

      <v-card-text class="pa-4">
        <v-row dense>
          <v-col cols="12" md="4">
            <div class="form-section">
              <div class="section-title">Profile</div>

              <div class="profile-box">
                <img
                  class="profile-image"
                  v-if="imageUrl"
                  :src="imageUrl"
                  alt="User"
                />

                <img
                  class="profile-image"
                  v-else
                  src="@/assets/images/user.jpeg"
                  alt="User"
                />

                <div class="profile-name">
                  {{ User.name || "User" }}

                  <v-tooltip bottom v-if="User.superAdmin">
                    <template v-slot:activator="{ on }">
                      <v-icon small color="amber accent-4" v-on="on">
                        mdi-crown
                      </v-icon>
                    </template>
                    <span>Super Admin</span>
                  </v-tooltip>
                </div>

                <div class="profile-email">
                  {{ User.email || "Login email" }}
                </div>

                <div class="profile-actions">
                  <v-btn
                    small
                    rounded
                    outlined
                    color="primary"
                    @click="pickFile"
                  >
                    <v-icon left small>mdi-paperclip</v-icon>
                    Upload
                  </v-btn>

                  <v-btn
                    small
                    rounded
                    outlined
                    color="error"
                    @click="removeImage"
                  >
                    <v-icon left small>mdi-delete</v-icon>
                    Remove
                  </v-btn>

                  <v-btn
                    v-if="currentUser.id !== User.id"
                    small
                    rounded
                    outlined
                    color="error"
                    @click="deleteUser"
                  >
                    <v-icon left small>mdi-trash-can-outline</v-icon>
                    Delete
                  </v-btn>
                </div>

                <div class="mt-4" v-if="User.id">
                  <ChangePassword :UserId="User.id" />
                </div>

                <input
                  type="file"
                  ref="image"
                  style="display: none"
                  accept="image/*"
                  @change="onFilePicked"
                />
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="4">
            <div class="form-section">
              <div class="section-title-row">
                <div class="section-title mb-0">User Information</div>

                <toggle-button
                  v-if="!User.superAdmin && currentUser.isAdmin"
                  :width="70"
                  :labels="{ checked: 'Admin', unchecked: 'User' }"
                  v-model="User.isAdmin"
                  :value="User.isAdmin"
                  :sync="true"
                />
              </div>

              <div class="field-label mt-4">Full Name</div>
              <v-text-field
                :error-messages="nameErrors"
                v-model="User.name"
                label="Full name"
                outlined
                dense
                :hide-details="nameErrors.length === 0"
              />

              <div class="field-label mt-3">Login Email</div>
              <v-text-field
                :error-messages="emailErrors"
                label="Login Email"
                outlined
                dense
                prepend-inner-icon="mdi-email-outline"
                :hide-details="emailErrors.length === 0"
                v-model="User.email"
              />

              <div class="field-label mt-3">Phone Number</div>
              <v-text-field
                label="Phone Number"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-phone-outline"
                v-model="User.phoneNumber"
              />

              <div class="field-label mt-3">Address</div>
              <v-text-field
                outlined
                dense
                hide-details
                label="Address"
                v-model="User.address"
              />

              <v-row dense>
                <v-col cols="12" sm="6">
                  <div class="field-label mt-3">City</div>
                  <v-text-field
                    outlined
                    dense
                    hide-details
                    label="City"
                    v-model="User.city"
                  />
                </v-col>

                <v-col cols="12" sm="3">
                  <div class="field-label mt-3">State</div>
                  <v-text-field
                    outlined
                    dense
                    hide-details
                    label="State"
                    v-model="User.state"
                  />
                </v-col>

                <v-col cols="12" sm="3">
                  <div class="field-label mt-3">Zip</div>
                  <v-text-field
                    outlined
                    dense
                    hide-details
                    label="Zip"
                    v-model="User.zip"
                  />
                </v-col>
              </v-row>
            </div>
          </v-col>

          <v-col
            cols="12"
            md="4"
            v-if="!User.superAdmin && !User.isAdmin"
          >
            <div class="form-section rights-section">
              <div class="section-title-row">
                <div>
                  <div class="section-title mb-0">User Rights</div>
                  <div class="section-subtitle">
                    <span class="green--text font-weight-bold">
                      {{ selected.length }}
                    </span>
                    /
                    <span class="blue--text font-weight-bold">
                      {{ rights.length }}
                    </span>
                    selected
                  </div>
                </div>

                <v-btn small text color="success" @click="allRights">
                  Give all
                </v-btn>
              </div>

              <v-text-field
                v-model="search"
                outlined
                class="mt-4 mb-2"
                prepend-inner-icon="search"
                dense
                label="Search rights"
                single-line
                hide-details
                clearable
              />

              <v-data-table
                v-model="selected"
                :mobile-breakpoint="0"
                :headers="headers"
                :items="rights"
                :search="search"
                item-key="name"
                show-select
                dense
                class="rights-table"
                :items-per-page="8"
              />
            </div>
          </v-col>

          <v-col
            cols="12"
            md="4"
            v-else
          >
            <div class="form-section rights-section">
              <div class="section-title">User Rights</div>
              <div class="section-subtitle">
                Rights are not shown for admin or super admin users.
              </div>
            </div>
          </v-col>

          <v-col cols="12">
            <div class="form-section">
              <div class="section-title-row">
                <div>
                  <div class="section-title mb-0">Mark Up Price</div>
                  <div class="section-subtitle">
                    Optional user price markup.
                  </div>
                </div>

                <toggle-button
                  :width="76"
                  :labels="{ checked: 'Enable', unchecked: 'Disable' }"
                  v-model="User.priceMarkupStatus"
                  :value="User.priceMarkupStatus"
                  :sync="true"
                />
              </div>

              <div class="field-label mt-4">Mark Up Percentage</div>
              <v-text-field
                v-model.number="User.priceMarkup"
                :disabled="!User.priceMarkupStatus"
                outlined
                dense
                type="number"
                min="1"
                max="100"
                step="1"
                :hide-details="priceMarkupErrors.length === 0"
                :error-messages="priceMarkupErrors"
                @blur="$v.User.priceMarkup.$touch()"
                @input="$v.User.priceMarkup.$touch()"
              >
                <template v-slot:append>
                  <v-icon>mdi-percent</v-icon>
                </template>
              </v-text-field>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions class="user-create-actions">
        <v-btn color="primary" text @click="cancel">
          Cancel
        </v-btn>

        <v-spacer />

        <v-btn
          color="primary"
          depressed
          rounded
          dark
          :loading="loading"
          @click="save"
        >
          Update User
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import userService from "../service.js";
import ChangePassword from "./ChangePassword";

const { validationMixin } = require("vuelidate");
const {
  required,
  minLength,
  email,
  integer,
  between,
} = require("vuelidate/lib/validators");

export default {
  name: "edit-user",

  components: {
    ChangePassword,
  },

  mixins: [validationMixin],

  data() {
    return {
      loading: false,
      search: null,
      selected: [],
      imageName: "",
      imageUrl: "",
      imageFile: "",
      isLoading: true,

      headers: [
        {
          text: "Access",
          align: "start",
          sortable: false,
          value: "name",
        },
        {
          text: "Type",
          value: "Type.name",
        },
      ],

      User: {
        name: null,
        isAdmin: false,
        phoneNumber: null,
        email: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        active: true,
        userRights: null,
        imageUrl: null,
        image: null,
        priceMarkupStatus: false,
        priceMarkup: null,
      },

      rights: [],
    };
  },

  validations: {
    User: {
      name: {
        required,
        minLength: minLength(2),
      },
      email: {
        required,
        email,
      },
      priceMarkup: {
        integer,
        between: between(1, 100),
      },
    },
  },

  computed: {
    ...mapGetters("global", ["currentUser"]),

    nameErrors() {
      const errors = [];

      if (!this.$v.User.name.$dirty) {
        return errors;
      }

      if (!this.$v.User.name.required) {
        errors.push(this.$t("validations.fieldIsRequired"));
      }

      if (!this.$v.User.name.minLength) {
        errors.push("Name must be least 2 character long");
      }

      return errors;
    },

    emailErrors() {
      const errors = [];

      if (!this.$v.User.email.$dirty) {
        return errors;
      }

      if (!this.$v.User.email.required) {
        errors.push(this.$t("validations.fieldIsRequired"));
      }

      if (!this.$v.User.email.email) {
        errors.push(this.$t("validations.emailIsNotValid"));
      }

      return errors;
    },

    priceMarkupErrors() {
      const errors = [];
      const f = this.$v.User.priceMarkup;

      if (!f.$dirty || !this.User.priceMarkupStatus) {
        return errors;
      }

      if (!f.integer) {
        errors.push("Must be an integer");
      }

      if (!f.between) {
        errors.push("Must be between 1 and 100");
      }

      return errors;
    },
  },

  watch: {
    imageUrl(val) {
      this.User.image = val;
    },

    "User.priceMarkupStatus"(val) {
      if (!val) {
        this.User.priceMarkup = null;
        this.$v.User.priceMarkup.$reset();
      }
    },
  },

  async created() {
    await this.getAllUserRights();
    await this.loadUser();
    this.isLoading = false;
  },

  methods: {
    allRights() {
      this.selected = this.rights;
    },

    pickFile() {
      this.$refs.image.click();
    },

    removeImage() {
      this.imageName = "";
      this.imageFile = "";
      this.imageUrl = "";
      this.User.image = null;
      this.User.imageUrl = null;

      if (this.$refs.image) {
        this.$refs.image.value = null;
      }
    },

    onFilePicked(e) {
      const files = e.target.files;

      if (files[0] !== undefined) {
        this.imageName = files[0].name;

        if (this.imageName.lastIndexOf(".") <= 0) {
          return;
        }

        const fr = new FileReader();

        fr.readAsDataURL(files[0]);
        fr.addEventListener("load", () => {
          this.imageUrl = fr.result;
          this.User.image = this.imageUrl;
          this.imageFile = files[0];
        });
      } else {
        this.removeImage();
      }
    },

    cancel() {
      this.$router.go(-1);
    },

    async getAllUserRights() {
      try {
        const result = await userService.getAllUserRights();
        this.rights = result.data || [];
      } catch (error) {
        console.error("getAllUserRights failed", error);
        this.rights = [];
      }
    },

    async loadUser() {
      if (!this.$route.params || !this.$route.params.id) {
        return this.$router.push("/user/list");
      }

      try {
        const id = this.$route.params.id;
        const response = await userService.getById(id);

        this.User = response.data || this.User;

        if (this.User.imageUrl) {
          this.imageUrl = this.User.imageUrl;
        }

        this.setPreAssignedRights();
      } catch (error) {
        console.error("loadUser failed", error);
        this.$router.push("/user/list");
      }
    },

    setPreAssignedRights() {
      this.selected = [];

      if (!this.User.userRights) {
        return;
      }

      const userRights = String(this.User.userRights).split(",");

      userRights.forEach((rightNumber) => {
        const right = this.rights.find((item) => item.number == rightNumber);

        if (right) {
          this.selected.push(right);
        }
      });
    },

    deleteUser() {
      if (!this.User.superAdmin) {
        if (
          this.User.id === this.currentUser.id ||
          this.currentUser.superAdmin ||
          this.currentUser.isAdmin
        ) {
          this.removeUser();
        } else {
          this.$swal(
            "Delete User",
            "You are not allow to delete other user account",
            "error"
          );
        }
      } else {
        if (this.currentUser.superAdmin) {
          this.removeUser();
        } else {
          this.$swal("Delete User", "Super admin can't be delete", "error");
        }
      }
    },

    removeUser() {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then((result) => {
        if (result.value) {
          this.$Progress.start();

          return userService
            .delete(this.User.id)
            .then((response) => {
              if (response.data) {
                this.$swal("Deleted!", "User has been deleted.", "success");
                this.$Progress.finish();
                this.$router.go(-1);
              }
            })
            .catch((error) => {
              const message =
                error && error.data && error.data.message
                  ? error.data.message
                  : "Unable to delete user";

              this.$swal
                .mixin({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                })
                .fire({
                  icon: "error",
                  title: message,
                });

              this.$Progress.fail();
            });
        }
      });
    },

    async save() {
      const newRights = [];

      this.selected.forEach((x) => {
        newRights.push(x.number);
      });

      this.User.userRights = newRights.toString();

      this.$v.$touch();

      if (this.User.priceMarkupStatus && !this.User.priceMarkup) {
        this.$v.User.priceMarkup.$touch();
      }

      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });

        return;
      }

      this.loading = true;

      const payload = {
        ...this.User,
      };

      delete payload.password;

      try {
        await userService.update(payload.id, payload);

        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "success",
            title: "User was updated!",
          });

        if (this.currentUser.id === this.User.id) {
          this.$router.push("/logout");
        } else {
          this.cancel();
        }
      } catch (err) {
        const message =
          err && err.data && err.data.message
            ? err.data.message
            : "Unable to update user";

        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: message,
          });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.user-create-page {
  background: #f8fafc;
}

.user-create-card {
  border-radius: 20px;
  background: #ffffff;
  overflow: hidden;
}

.user-create-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.active-label {
  font-size: 13px;
  font-weight: 700;
  color: #475569;
}

.form-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px;
  height: 100%;
}

.section-title {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 12px;
}

.section-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.section-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.field-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
}

.profile-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.profile-image {
  width: 140px;
  height: 160px;
  object-fit: cover;
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
}

.profile-name {
  margin-top: 12px;
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 4px;
}

.profile-email {
  margin-top: 2px;
  font-size: 13px;
  color: #64748b;
  word-break: break-word;
}

.profile-actions {
  margin-top: 14px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.rights-section {
  min-height: 100%;
}

.rights-table {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  background: #ffffff;
}

.user-create-actions {
  padding: 12px 16px;
}

@media (max-width: 600px) {
  .user-create-header {
    padding: 14px 16px;
    align-items: flex-start;
  }

  .page-title {
    font-size: 20px;
  }

  .form-section {
    padding: 12px;
    height: auto;
  }

  .header-actions {
    width: 100%;
    justify-content: space-between;
  }
}
</style>